import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useConfirm } from "../context/ConfirmContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Plus, Trash2, Trophy, Sparkles, Shuffle, Send, CalendarDays, AlertTriangle, Crown, Ticket, Camera, X } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

function monthLabel(m) {
  if (!m) return "—";
  const [y, mm] = m.split("-");
  return `${MONTHS[parseInt(mm, 10) - 1]} ${y}`;
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function dayOfMonth() {
  return new Date().getDate();
}

export default function RaffleAdmin() {
  const [raffles, setRaffles] = useState([]);
  const [month, setMonth] = useState(currentMonth());
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [winnersDialog, setWinnersDialog] = useState(null);

  const loadList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/raffles");
      setRaffles(data || []);
    } finally { setLoading(false); }
  };
  const loadDetail = async (m) => {
    try {
      const { data } = await api.get(`/admin/raffles/${m}`);
      setDetail(data);
    } catch (err) {
      console.warn("RaffleAdmin: failed to load detail for", m, err?.response?.data || err?.message);
      setDetail(null);
    }
  };

  useEffect(() => { loadList(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadDetail(month); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => { loadList(); loadDetail(month); };

  return (
    <div className="space-y-5">
      {/* Header card */}
      <div className="bg-white border border-border rounded-2xl p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-300 to-amber-500 grid place-items-center">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-display text-2xl tracking-tight">Rifa mensual de OFERTAS México</h3>
              <p className="text-xs text-muted-foreground">
                Configura premios, publícalos a clientes y realiza el sorteo cada fin de mes.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">Mes:</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="raffle-month-input" />
          </div>
        </div>

        {/* Reminder if early in month & not published */}
        {detail && month === currentMonth() && !detail.published && dayOfMonth() <= 3 && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-xs text-amber-900">
            <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              <strong>Recordatorio:</strong> Estamos en los primeros días del mes — captura y publica los premios cuanto antes para que los clientes vean qué se rifa.
            </span>
          </div>
        )}
        {detail && month === currentMonth() && !detail.published && dayOfMonth() > 3 && (
          <div className="mt-4 bg-rose-50 border border-rose-200 rounded-lg p-3 flex items-start gap-2 text-xs text-rose-900">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              <strong>Atención:</strong> Ya pasaron los primeros 3 días del mes y los premios no están publicados. Los clientes ya están acumulando boletos sin saber qué pueden ganar.
            </span>
          </div>
        )}
      </div>

      {/* Editor */}
      {detail && <RaffleEditor detail={detail} onChange={refresh} />}

      {/* List of past raffles */}
      <div className="bg-white border border-border rounded-2xl p-6">
        <h3 className="font-display text-xl tracking-tight mb-4">Historial de rifas</h3>
        {loading ? <div className="text-muted-foreground text-sm">Cargando…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b border-border">
                <tr><th className="py-2">Mes</th><th>Premios</th><th>Boletos</th><th>Fecha sorteo</th><th>Estado</th><th></th></tr>
              </thead>
              <tbody>
                {raffles.map((r) => (
                  <tr key={r.month} className="border-b border-border" data-testid={`raffle-history-${r.month}`}>
                    <td className="py-3 font-medium">{monthLabel(r.month)}{r.is_current && <Badge className="ml-2 bg-[#7C3AED] text-white">Actual</Badge>}</td>
                    <td>{(r.prizes || []).length}</td>
                    <td><Badge variant="outline">{r.tickets_active}</Badge> activos · <span className="text-xs text-muted-foreground">{r.tickets_archived} archivados</span></td>
                    <td className="text-xs">{r.draw_date}</td>
                    <td>
                      {r.drawn_at ? (
                        <Badge className="bg-emerald-500 text-white">Sorteada</Badge>
                      ) : r.published ? (
                        <Badge className="bg-amber-300 text-amber-900">Publicada · pendiente</Badge>
                      ) : (
                        <Badge variant="outline">Borrador</Badge>
                      )}
                    </td>
                    <td className="text-right">
                      {r.drawn_at && (
                        <Button size="sm" variant="outline" onClick={() => setWinnersDialog(r)} data-testid={`raffle-winners-btn-${r.month}`}>
                          <Crown className="w-3.5 h-3.5 mr-1" /> Ganadores
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <WinnersDialog raffle={winnersDialog} onClose={() => setWinnersDialog(null)} />
    </div>
  );
}

function RaffleEditor({ detail, onChange }) {
  const confirm = useConfirm();
  const [prizes, setPrizes] = useState(detail.prizes && detail.prizes.length > 0 ? detail.prizes.map((p) => p.label) : [""]);
  const [drawDate, setDrawDate] = useState(detail.draw_date || "");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [drawing, setDrawing] = useState(false);

  // Keep editor in sync when month changes
  useEffect(() => {
    setPrizes(detail.prizes && detail.prizes.length > 0 ? detail.prizes.map((p) => p.label) : [""]);
    setDrawDate(detail.draw_date || "");
  }, [detail.month]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDrawn = !!detail.drawn_at;

  const addPrize = () => setPrizes((arr) => arr.length < 10 ? [...arr, ""] : arr);
  const removePrize = (i) => setPrizes((arr) => arr.filter((_, idx) => idx !== i));
  const setPrize = (i, v) => setPrizes((arr) => arr.map((p, idx) => idx === i ? v : p));

  const save = async () => {
    const filled = prizes.map((p) => p.trim()).filter(Boolean);
    if (filled.length === 0) return toast.error("Captura al menos un premio");
    setSaving(true);
    try {
      await api.put(`/admin/raffles/${detail.month}`, {
        prizes: filled.map((label) => ({ label })),
        draw_date: drawDate || null,
      });
      toast.success("Premios guardados");
      onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      await api.post(`/admin/raffles/${detail.month}/publish`);
      toast.success("¡Premios publicados! Los clientes ya pueden verlos.");
      onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setPublishing(false); }
  };

  const unpublish = async () => {
    if (!await confirm("¿Despublicar los premios? Los clientes dejarán de verlos.")) return;
    try {
      await api.post(`/admin/raffles/${detail.month}/unpublish`);
      toast.success("Despublicado");
      onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const draw = async () => {
    if (!await confirm(`¿Realizar el sorteo de ${monthLabel(detail.month)} ahora? Los boletos participantes se archivarán después del sorteo y los clientes serán notificados.`, { title: "Realizar sorteo" })) return;
    setDrawing(true);
    try {
      const { data } = await api.post(`/admin/raffles/${detail.month}/draw`);
      toast.success(`Sorteo realizado · ${data.winners.length} ganador(es)`);
      onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setDrawing(false); }
  };

  return (
    <div className="bg-white border border-border rounded-2xl p-6" data-testid="raffle-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="font-display text-xl tracking-tight">{monthLabel(detail.month)}</h3>
          <div className="text-xs text-muted-foreground mt-1">
            <Ticket className="inline w-3.5 h-3.5 mr-1" />
            <strong className="text-[#5B21B6]">{detail.tickets_active}</strong> boletos participando
            {detail.tickets_archived > 0 && <span> · {detail.tickets_archived} archivados</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {detail.published && !isDrawn && <Badge className="bg-amber-300 text-amber-900">Publicada</Badge>}
          {isDrawn && <Badge className="bg-emerald-500 text-white"><Crown className="w-3 h-3 mr-1" />Sorteada</Badge>}
          {!detail.published && !isDrawn && <Badge variant="outline">Borrador</Badge>}
        </div>
      </div>

      {/* Prizes editor */}
      <div className="space-y-2">
        <Label className="text-sm">Premios del mes (1 a 10)</Label>
        {prizes.map((p, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`prize-row-${i}`}>
            <div className="w-9 h-9 rounded-full bg-amber-100 border border-amber-200 grid place-items-center text-amber-900 font-display shrink-0">
              {i + 1}
            </div>
            <Input
              value={p}
              onChange={(e) => setPrize(i, e.target.value)}
              placeholder={`Premio ${i + 1} — ej. Refrigerador 16 pies`}
              disabled={isDrawn}
              data-testid={`prize-input-${i}`}
            />
            {prizes.length > 1 && !isDrawn && (
              <Button variant="outline" size="sm" onClick={() => removePrize(i)} className="text-rose-600" data-testid={`prize-remove-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        ))}
        {!isDrawn && prizes.length < 10 && (
          <Button variant="outline" size="sm" onClick={addPrize} className="mt-2" data-testid="prize-add">
            <Plus className="w-3.5 h-3.5 mr-1" /> Agregar premio
          </Button>
        )}
      </div>

      <div className="mt-5">
        <Label className="text-sm">Fecha del sorteo</Label>
        <Input
          type="date"
          value={drawDate}
          onChange={(e) => setDrawDate(e.target.value)}
          disabled={isDrawn}
          className="w-56"
          data-testid="raffle-draw-date"
        />
        <div className="text-[11px] text-muted-foreground mt-1">
          <CalendarDays className="inline w-3 h-3 mr-1" />
          Default: último día del mes. Solo informativo (el sorteo se ejecuta cuando aprietas "Realizar sorteo").
        </div>
      </div>

      {/* Actions */}
      {!isDrawn && (
        <div className="mt-6 flex items-center gap-2 flex-wrap">
          <Button onClick={save} disabled={saving} className="rounded-full" variant="outline" data-testid="raffle-save-btn">
            {saving ? "Guardando…" : "Guardar premios"}
          </Button>
          {!detail.published ? (
            <Button onClick={publish} disabled={publishing} className="btn-primary rounded-full" data-testid="raffle-publish-btn">
              <Send className="w-4 h-4 mr-2" />
              {publishing ? "Publicando…" : "Publicar premios de la rifa"}
            </Button>
          ) : (
            <Button onClick={unpublish} variant="outline" className="rounded-full text-rose-600" data-testid="raffle-unpublish-btn">
              Despublicar
            </Button>
          )}
          <div className="flex-1" />
          <Button onClick={draw} disabled={drawing || detail.tickets_active === 0} className="bg-gradient-to-r from-amber-400 to-amber-600 text-white rounded-full hover:from-amber-500 hover:to-amber-700" data-testid="raffle-draw-btn">
            <Shuffle className="w-4 h-4 mr-2" />
            {drawing ? "Sorteando…" : `Realizar sorteo (${detail.tickets_active} boletos)`}
          </Button>
        </div>
      )}

      {isDrawn && (
        <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Crown className="w-5 h-5 text-amber-600" />
            <div className="font-display text-lg">Ganadores del sorteo</div>
            <span className="text-xs text-muted-foreground ml-2">Sube la foto de la entrega para mostrarla en /ganadores</span>
          </div>
          <ul className="space-y-2" data-testid="raffle-winners">
            {(detail.winners || []).map((w) => (
              <WinnerRow key={w.ticket_id} winner={w} month={detail.month} onChange={onChange} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function WinnerRow({ winner, month, onChange }) {
  const confirm = useConfirm();
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen pesa más de 5MB");
      return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.post(`/admin/raffles/${month}/winners/${winner.ticket_id}/photo`, { photo_base64: dataUrl });
      toast.success("Foto subida — ya visible en /ganadores");
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir foto");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = async () => {
    if (!await confirm("¿Quitar la foto de este ganador?")) return;
    try {
      await api.delete(`/admin/raffles/${month}/winners/${winner.ticket_id}/photo`);
      toast.success("Foto eliminada");
      onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <li className="bg-white rounded-lg p-3 flex items-start gap-3" data-testid={`winner-${winner.position}`}>
      <div className="w-9 h-9 rounded-full bg-amber-200 grid place-items-center text-amber-900 font-display shrink-0">
        {winner.position}
      </div>
      <div className="flex-1">
        <div className="font-medium">{winner.prize_label}</div>
        <div className="text-xs text-muted-foreground">
          Ganador: <strong>{winner.customer_name}</strong> · {winner.customer_whatsapp} · comercio: {winner.business_name}
        </div>
        <div className="text-[10px] font-mono text-zinc-400 mt-0.5">Boleto: {winner.ticket_id}</div>
      </div>
      <div className="shrink-0">
        {winner.photo_base64 ? (
          <div className="relative group">
            <img src={winner.photo_base64} alt="Entrega" className="w-20 h-20 rounded-lg object-cover border border-border" />
            <button
              onClick={removePhoto}
              className="absolute -top-2 -right-2 w-6 h-6 bg-rose-500 hover:bg-rose-600 text-white rounded-full grid place-items-center shadow-md"
              title="Quitar foto"
              data-testid={`winner-photo-remove-${winner.position}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <label className="cursor-pointer inline-flex items-center gap-1 text-xs bg-[#F5F3FF] hover:bg-[#EDE9FE] border border-[#DDD6FE] text-[#5B21B6] rounded-full px-3 py-1.5 transition-colors" data-testid={`winner-photo-upload-${winner.position}`}>
            <Camera className="w-3.5 h-3.5" />
            {uploading ? "Subiendo…" : "Subir foto"}
            <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
          </label>
        )}
      </div>
    </li>
  );
}

function WinnersDialog({ raffle, onClose }) {
  if (!raffle) return null;
  return (
    <Dialog open={!!raffle} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="winners-dialog">
        <DialogHeader>
          <DialogTitle>Ganadores · {monthLabel(raffle.month)}</DialogTitle>
        </DialogHeader>
        <ul className="space-y-2">
          {(raffle.winners || []).map((w) => (
            <li key={w.ticket_id} className="border border-border rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Crown className="w-4 h-4 text-amber-500" />
                <span className="font-display text-sm">Premio {w.position}: {w.prize_label}</span>
              </div>
              <div className="text-xs text-muted-foreground">
                {w.customer_name} · {w.customer_whatsapp} · {w.business_name}
              </div>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
